// CampaignSupervisionPOC.js — Documentation Fonctionnelle : POC « Supervision des campagnes » (module Campagne).
// Aperçu interactif de l'écran de supervision (tableau de bord live) : en-tête live + actualisation,
// 4 cartes KPI, 2 graphiques (résultats de test + répartition par statut), section « Campagnes actives »
// et section « Exécutions récentes », doublé d'un guide utilisateur et d'une référence des indicateurs/API.
// Disposition calquée sur l'écran réel ; actions SIMULÉES localement (aucun appel réseau ; graphiques = divs maison).
// Source de vérité : automata-studio-frontend [CPG][01]-Supervision.js + automata_campaign (Campaign/Execution).
import React, { useState } from 'react';
import {
  Activity, RefreshCw, List, Target, Zap, FileText, Calendar, Clock, Play, Pause,
  CheckCircle, XCircle, AlertTriangle, BarChart2, ArrowRight, Info, Eye,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, Icon, SearchBar, EmptyState } from '../ui';

// Libellés et teintes des statuts (calqués sur STATUS_LABELS / statusColorMap de l'écran réel).
const STATUS_META = {
  ACTIVE: { label: 'Active', color: '#10B981' },
  IN_PROGRESS: { label: 'En cours', color: '#3B82F6' },
  DRAFT: { label: 'Brouillon', color: '#6B7280' },
  RUNNING: { label: 'En cours', color: '#3B82F6' },
  PAUSED: { label: 'En pause', color: '#F59E0B' },
  COMPLETED_SUCCESS: { label: 'Réussi', color: '#10B981' },
  COMPLETED_WITH_FAILURES: { label: 'Terminé avec échecs', color: '#F59E0B' },
  COMPLETED_WITH_ERRORS: { label: 'Terminé avec erreurs', color: '#EF4444' },
};
const statusOf = (s) => STATUS_META[s] || { label: s, color: '#6B7280' };

// Libellés courts pour le graphe « Répartition par statut » (calqués sur STATUS_CHART_LABELS).
const STATUS_CHART_LABELS = {
  ACTIVE: 'Actives', IN_PROGRESS: 'En cours', DRAFT: 'Brouillons',
  PAUSED: 'En pause', COMPLETED: 'Terminées', ARCHIVED: 'Archivées', CANCELLED: 'Annulées',
};

// Jeu de données en dur (deux instantanés pour simuler le rafraîchissement live).
const SNAPSHOTS = [
  {
    stats: { totalCampaigns: 12, activeCampaigns: 4, completedCampaigns: 6, successRate: 87, totalTestCases: 148, passedTests: 132, failedTests: 14, skippedTests: 6 },
    statusCount: { ACTIVE: 4, IN_PROGRESS: 1, PAUSED: 1, DRAFT: 1, COMPLETED: 5, ARCHIVED: 0 },
    campaigns: [
      { id: 1, campaignId: 'CPG-2026-014', name: 'Recette virements CEMAC', status: 'IN_PROGRESS', testCases: 24, created: '2026-06-10' },
      { id: 2, campaignId: 'CPG-2026-013', name: 'Non-régression GDC Amplitude', status: 'ACTIVE', testCases: 38, created: '2026-06-08' },
      { id: 3, campaignId: 'CPG-2026-012', name: 'Tests API REST paiements', status: 'PAUSED', testCases: 17, created: '2026-06-05' },
      { id: 4, campaignId: 'CPG-2026-011', name: 'Contrôles base Oracle', status: 'ACTIVE', testCases: 12, created: '2026-06-03' },
    ],
    executions: [
      { id: 91, executionId: 'EXE-091', name: 'Recette virements CEMAC', campaign: 'Recette virements CEMAC', status: 'RUNNING', passed: 18, failed: 2, skipped: 1, started: '2026-06-15 09:42' },
      { id: 90, executionId: 'EXE-090', name: 'NR GDC Amplitude', campaign: 'Non-régression GDC Amplitude', status: 'COMPLETED_SUCCESS', passed: 38, failed: 0, skipped: 0, started: '2026-06-14 17:10' },
      { id: 89, executionId: 'EXE-089', name: 'API REST paiements', campaign: 'Tests API REST paiements', status: 'COMPLETED_WITH_FAILURES', passed: 13, failed: 4, skipped: 0, started: '2026-06-14 11:05' },
      { id: 88, executionId: 'EXE-088', name: 'Contrôles base Oracle', campaign: 'Contrôles base Oracle', status: 'COMPLETED_WITH_ERRORS', passed: 6, failed: 5, skipped: 1, started: '2026-06-13 15:30' },
    ],
  },
  {
    stats: { totalCampaigns: 12, activeCampaigns: 4, completedCampaigns: 6, successRate: 89, totalTestCases: 148, passedTests: 141, failedTests: 9, skippedTests: 4 },
    statusCount: { ACTIVE: 5, IN_PROGRESS: 1, PAUSED: 0, DRAFT: 1, COMPLETED: 5, ARCHIVED: 0 },
    campaigns: [
      { id: 1, campaignId: 'CPG-2026-014', name: 'Recette virements CEMAC', status: 'IN_PROGRESS', testCases: 24, created: '2026-06-10' },
      { id: 2, campaignId: 'CPG-2026-013', name: 'Non-régression GDC Amplitude', status: 'ACTIVE', testCases: 38, created: '2026-06-08' },
      { id: 3, campaignId: 'CPG-2026-012', name: 'Tests API REST paiements', status: 'ACTIVE', testCases: 17, created: '2026-06-05' },
      { id: 4, campaignId: 'CPG-2026-011', name: 'Contrôles base Oracle', status: 'ACTIVE', testCases: 12, created: '2026-06-03' },
    ],
    executions: [
      { id: 91, executionId: 'EXE-091', name: 'Recette virements CEMAC', campaign: 'Recette virements CEMAC', status: 'COMPLETED_SUCCESS', passed: 23, failed: 0, skipped: 1, started: '2026-06-15 09:42' },
      { id: 92, executionId: 'EXE-092', name: 'API REST paiements', campaign: 'Tests API REST paiements', status: 'RUNNING', passed: 9, failed: 1, skipped: 0, started: '2026-06-15 09:58' },
      { id: 90, executionId: 'EXE-090', name: 'NR GDC Amplitude', campaign: 'Non-régression GDC Amplitude', status: 'COMPLETED_SUCCESS', passed: 38, failed: 0, skipped: 0, started: '2026-06-14 17:10' },
      { id: 88, executionId: 'EXE-088', name: 'Contrôles base Oracle', campaign: 'Contrôles base Oracle', status: 'COMPLETED_WITH_ERRORS', passed: 6, failed: 5, skipped: 1, started: '2026-06-13 15:30' },
    ],
  },
];

// Liens de navigation de l'en-tête (calqués sur navLinks de l'écran réel).
const NAV_LINKS = [
  { label: 'Campagnes', icon: List },
  { label: 'Cas de test', icon: FileText },
  { label: 'Exécutions', icon: Play },
  { label: 'Rapports', icon: BarChart2 },
];

// ---- Petits sous-composants de la réplique ---------------------------------
const StatusPill = ({ status }) => {
  const m = statusOf(status);
  return <Badge color={m.color}>{m.label}</Badge>;
};

// Barre de progression réussis/échecs/ignorés (équivalent MiniProgressBar de l'écran réel).
const ResultBar = ({ passed = 0, failed = 0, skipped = 0 }) => {
  const total = passed + failed + skipped;
  if (total === 0) return <span className="text-xs text-gray-400 dark:text-gray-500">-</span>;
  const p = (passed / total) * 100;
  const f = (failed / total) * 100;
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex-1 min-w-[64px] h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex">
        {p > 0 && <div className="h-full bg-emerald-500" style={{ width: `${p}%` }} />}
        {f > 0 && <div className="h-full bg-red-500" style={{ width: `${f}%` }} />}
        {100 - p - f > 0 && <div className="h-full bg-amber-400" style={{ width: `${100 - p - f}%` }} />}
      </div>
      <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap">{passed}/{total}</span>
    </div>
  );
};

const ExecIcon = ({ status }) => {
  switch (status) {
    case 'RUNNING': return <Play className="h-4 w-4 text-blue-500" />;
    case 'PAUSED': return <Pause className="h-4 w-4 text-amber-500" />;
    case 'COMPLETED_SUCCESS': return <CheckCircle className="h-4 w-4 text-emerald-500" />;
    case 'COMPLETED_WITH_FAILURES': return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    case 'COMPLETED_WITH_ERRORS': return <XCircle className="h-4 w-4 text-red-500" />;
    default: return <Clock className="h-4 w-4 text-gray-400" />;
  }
};

// En-tête de section (calqué sur les bandeaux des GlassCards : icône + titre + pastille + « Voir tout »).
const SectionHeader = ({ icon: IconCmp, title, count }) => (
  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
    <div className="flex items-center gap-2">
      <IconCmp className="h-[18px] w-[18px] text-violet-500" />
      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h3>
      {count > 0 && (
        <span className="px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 text-xs font-medium">{count}</span>
      )}
    </div>
    <button type="button" className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-violet-300 dark:border-violet-600 text-violet-600 dark:text-violet-400 text-xs font-medium hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors">
      Voir tout <ArrowRight className="h-3.5 w-3.5" />
    </button>
  </div>
);

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-violet-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// ---- Page -------------------------------------------------------------------
const CampaignSupervisionPOC = () => {
  const [snapIndex, setSnapIndex] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [search, setSearch] = useState('');
  const [execFilter, setExecFilter] = useState(null); // 'failures' | 'running' | null

  const snap = SNAPSHOTS[snapIndex];
  const { stats } = snap;
  const totalTests = stats.passedTests + stats.failedTests + stats.skippedTests;
  const runningCount = snap.executions.filter((e) => e.status === 'RUNNING').length;

  // Rafraîchissement SIMULÉ : bascule d'instantané + animation, aucun réseau.
  const refresh = () => {
    setRefreshing(true);
    window.setTimeout(() => {
      setSnapIndex((i) => (i + 1) % SNAPSHOTS.length);
      setRefreshCount((c) => c + 1);
      setRefreshing(false);
    }, 700);
  };

  const filteredCampaigns = snap.campaigns.filter((c) =>
    `${c.name} ${c.campaignId} ${statusOf(c.status).label}`.toLowerCase().includes(search.toLowerCase()));
  const filteredExecutions = snap.executions.filter((e) => {
    if (execFilter === 'failures') return e.status === 'COMPLETED_WITH_FAILURES' || e.status === 'COMPLETED_WITH_ERRORS';
    if (execFilter === 'running') return e.status === 'RUNNING';
    return true;
  });

  const kpiCards = [
    { title: 'Campagnes totales', value: stats.totalCampaigns, icon: List, gradient: 'from-violet-500 to-purple-700', progress: stats.totalCampaigns ? (stats.activeCampaigns / stats.totalCampaigns) * 100 : 0, subLeft: `${stats.activeCampaigns} actives`, subRight: `${stats.completedCampaigns} terminées` },
    { title: 'Taux de réussite', value: `${stats.successRate}%`, icon: Target, gradient: 'from-emerald-500 to-green-700', progress: totalTests ? (stats.passedTests / totalTests) * 100 : 0, subCenter: `${stats.passedTests} réussis sur ${totalTests}` },
    { title: 'Exécutions actives', value: runningCount, icon: Zap, gradient: 'from-amber-500 to-orange-700', progress: runningCount ? 100 : 0, subCenter: runningCount ? "En cours d'exécution" : 'Aucune en cours' },
    { title: 'Cas de test', value: stats.totalTestCases, icon: FileText, gradient: 'from-rose-500 to-pink-700', progress: 0, subCenter: 'Dans toutes les campagnes' },
  ];

  // Répartition des résultats — rendue en anneau « maison » (conic-gradient) + légende, sans librairie.
  const distribution = [
    { name: 'Réussis', value: stats.passedTests, color: '#10B981' },
    { name: 'Échoués', value: stats.failedTests, color: '#EF4444' },
    { name: 'Ignorés', value: stats.skippedTests, color: '#F59E0B' },
  ].filter((d) => d.value > 0);

  // Construction des segments de l'anneau (conic-gradient cumulatif).
  let acc = 0;
  const ringStops = distribution.map((d) => {
    const start = totalTests ? (acc / totalTests) * 100 : 0;
    acc += d.value;
    const end = totalTests ? (acc / totalTests) * 100 : 0;
    return `${d.color} ${start}% ${end}%`;
  });
  const ringStyle = ringStops.length
    ? { background: `conic-gradient(${ringStops.join(', ')})` }
    : undefined;

  // Répartition par statut — barres verticales « maison » (équivalent du BarChart recharts).
  const statusBars = Object.entries(snap.statusCount)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: STATUS_CHART_LABELS[k] || k, count: v }));
  const maxStatus = Math.max(1, ...statusBars.map((b) => b.count));

  return (
    <div className="space-y-8">
      {/* (1) Intro */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Supervision des campagnes</h1>
          <Badge color="#F59E0B">Module Campagne</Badge>
          <Badge color="#3B82F6">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Aperçu interactif de l'écran de supervision des campagnes (démo, actions simulées localement).
        </p>
      </div>

      {/* (2) Aperçu interactif de l'IHM */}
      <Card className="overflow-hidden">
        {/* En-tête type app (bandeau live + navigation + actualiser) */}
        <div className="px-5 py-4 bg-gradient-to-r from-violet-600 to-purple-700 text-white flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center"><Activity className="h-6 w-6" /></span>
            <div>
              <h2 className="text-lg font-bold leading-tight">Supervision des campagnes</h2>
              <div className="flex items-center gap-2 text-xs text-white/80">
                <span className="relative flex h-2 w-2">
                  <span className={`absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 ${refreshing ? 'animate-ping' : ''}`} />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span className="text-emerald-200 font-medium">Live</span>
                <span>· {refreshCount === 0 ? 'Données initiales' : `Actualisé ${refreshCount} fois`}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-1 bg-white/10 rounded-xl p-1">
              {NAV_LINKS.map((link) => (
                <button key={link.label} type="button" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 text-xs font-medium transition-colors">
                  <link.icon className="h-4 w-4" />{link.label}
                </button>
              ))}
            </div>
            <Button onClick={refresh} disabled={refreshing} className="bg-white/20 hover:bg-white/30 text-white">
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />{refreshing ? 'Actualisation…' : 'Actualiser'}
            </Button>
          </div>
        </div>

        <CardContent className="space-y-6">
          {/* Cartes KPI (4 colonnes) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {kpiCards.map((kpi, i) => (
              <div key={i} className={`bg-gradient-to-br ${kpi.gradient} rounded-2xl p-5 text-white shadow-lg flex flex-col`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-3xl font-bold tracking-tight">{kpi.value}</p>
                    <p className="text-white/80 text-sm mt-0.5">{kpi.title}</p>
                  </div>
                  <span className="w-12 h-12 bg-white/15 rounded-2xl flex items-center justify-center"><kpi.icon className="h-6 w-6" /></span>
                </div>
                <div className="h-1.5 rounded-full bg-white/20 mt-auto overflow-hidden">
                  <div className="h-full bg-white/60 rounded-full transition-all duration-700" style={{ width: `${Math.min(kpi.progress, 100)}%` }} />
                </div>
                <div className="mt-2">
                  {kpi.subLeft && kpi.subRight ? (
                    <div className="flex justify-between"><span className="text-xs text-white/70">{kpi.subLeft}</span><span className="text-xs text-white/70">{kpi.subRight}</span></div>
                  ) : (
                    <p className="text-xs text-white/70 text-center">{kpi.subCenter}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Deux graphiques côte à côte (résultats de test + répartition par statut) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Résultats de test — anneau maison */}
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                <BarChart2 className="h-[18px] w-[18px] text-violet-500" />
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Résultats de test</h3>
                <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{totalTests} tests</span>
              </div>
              <div className="p-4 h-[280px] flex flex-col items-center justify-center">
                {distribution.length === 0 ? (
                  <>
                    <div className="relative w-32 h-32 mb-4 rounded-full border-[12px] border-gray-200 dark:border-gray-700 flex items-center justify-center">
                      <span className="text-lg font-bold text-gray-300 dark:text-gray-600">0%</span>
                    </div>
                    <p className="text-sm text-gray-400 dark:text-gray-500">Aucun résultat de test disponible</p>
                  </>
                ) : (
                  <>
                    <div className="relative w-36 h-36 rounded-full" style={ringStyle}>
                      <div className="absolute inset-[14px] rounded-full bg-white dark:bg-gray-800 flex flex-col items-center justify-center">
                        <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">{stats.successRate}%</span>
                        <span className="text-[11px] text-gray-400 dark:text-gray-500">réussite</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-4 flex-wrap justify-center">
                      {distribution.map((d) => (
                        <span key={d.name} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: d.color }} />{d.name} ({d.value})
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Répartition par statut — barres verticales maison */}
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                <BarChart2 className="h-[18px] w-[18px] text-violet-500" />
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Répartition par statut</h3>
                <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{stats.totalCampaigns} campagnes</span>
              </div>
              <div className="p-4 h-[280px]">
                {statusBars.length === 0 ? (
                  <EmptyState icon={BarChart2} title="Aucune campagne enregistrée" message="Créez votre première campagne pour suivre les statuts." />
                ) : (
                  <div className="h-full flex items-end justify-around gap-3 pt-4">
                    {statusBars.map((b) => (
                      <div key={b.name} className="flex-1 h-full flex flex-col items-center justify-end gap-2">
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 tabular-nums">{b.count}</span>
                        <div className="w-full max-w-[48px] rounded-t-lg bg-violet-500 dark:bg-violet-500 transition-all duration-700" style={{ height: `${(b.count / maxStatus) * 100}%` }} />
                        <span className="text-[11px] text-gray-500 dark:text-gray-400 text-center leading-tight">{b.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section « Campagnes actives » (table) */}
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <SectionHeader icon={Calendar} title="Campagnes actives" count={snap.campaigns.length} />
            <div className="p-4 space-y-3">
              <SearchBar value={search} onChange={setSearch} placeholder="Filtrer les campagnes…" className="w-full sm:w-72" />
              {filteredCampaigns.length === 0 ? (
                <EmptyState icon={Calendar} title="Aucune campagne active" message="Aucune campagne active ne correspond au filtre." />
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                      <tr>{['Nom', 'Statut', 'Cas de test', 'Créée le', 'Actions'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2.5 uppercase text-xs tracking-wider">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {filteredCampaigns.map((c) => (
                        <tr key={c.id} className="hover:bg-violet-50/50 dark:hover:bg-violet-900/10">
                          <td className="px-4 py-3"><div className="font-medium text-gray-800 dark:text-gray-200">{c.name}</div><div className="text-xs text-gray-400 dark:text-gray-500">{c.campaignId}</div></td>
                          <td className="px-4 py-3"><StatusPill status={c.status} /></td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{c.testCases} <span className="text-xs text-gray-400">tests</span></td>
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{c.created}</td>
                          <td className="px-4 py-3 text-right"><Button size="sm" className="bg-gradient-to-r from-violet-500 to-purple-600 text-white"><Play className="h-3 w-3" />Exécuter</Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Section « Exécutions récentes » (table) */}
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <SectionHeader icon={Clock} title="Exécutions récentes" count={snap.executions.length} />
            <div className="p-4 space-y-3">
              <div className="flex gap-2">
                {[['all', 'Toutes'], ['running', 'En cours'], ['failures', 'Échecs']].map(([k, lbl]) => (
                  <button key={k} type="button" onClick={() => setExecFilter(k === 'all' ? null : k)} className={`px-3 py-1 rounded-full text-xs font-medium ${((k === 'all' && !execFilter) || execFilter === k) ? 'bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>{lbl}</button>
                ))}
              </div>
              {filteredExecutions.length === 0 ? (
                <EmptyState icon={Play} title="Aucune exécution récente" message="Aucune exécution ne correspond au filtre sélectionné." />
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                      <tr>{['Exécution', 'Campagne', 'Statut', 'Résultats', 'Démarrée le'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2.5 uppercase text-xs tracking-wider">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {filteredExecutions.map((e) => (
                        <tr key={e.id} className="hover:bg-violet-50/50 dark:hover:bg-violet-900/10">
                          <td className="px-4 py-3"><div className="flex items-center gap-2.5"><ExecIcon status={e.status} /><div><div className="font-medium text-gray-800 dark:text-gray-200">{e.name}</div><div className="text-xs text-gray-400 dark:text-gray-500">{e.executionId}</div></div></div></td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{e.campaign}</td>
                          <td className="px-4 py-3"><StatusPill status={e.status} /></td>
                          <td className="px-4 py-3 min-w-[150px]"><ResultBar passed={e.passed} failed={e.failed} skipped={e.skipped} /></td>
                          <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400 whitespace-nowrap">{e.started}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* (3) Guide utilisateur */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Activity className="h-5 w-5 text-violet-500" />1. Surveiller les campagnes</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Lire les <strong>cartes KPI</strong> : campagnes totales/actives, taux de réussite, exécutions actives, cas de test.</GuideStep>
              <GuideStep n={2}>Consulter les graphiques <strong>Résultats de test</strong> et <strong>Répartition par statut</strong>.</GuideStep>
              <GuideStep n={3}>Cliquer sur <strong>Actualiser</strong> pour rafraîchir le tableau de bord (auto toutes les 60 s en réel). <button type="button" onClick={refresh} className="text-violet-600 dark:text-violet-400 underline">Essayer</button></GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Play className="h-5 w-5 text-violet-500" />2. Suivre une exécution</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Aller à la section <strong>Exécutions récentes</strong> et filtrer sur <strong>En cours</strong>. <button type="button" onClick={() => setExecFilter('running')} className="text-violet-600 dark:text-violet-400 underline">Filtrer</button></GuideStep>
              <GuideStep n={2}>Repérer le statut <strong>En cours</strong> et suivre la <strong>barre de progression</strong> (réussis/total).</GuideStep>
              <GuideStep n={3}>Cliquer sur une ligne pour ouvrir le <strong>rapport détaillé</strong> de l'exécution.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><AlertTriangle className="h-5 w-5 text-violet-500" />3. Identifier les échecs</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Dans <strong>Exécutions récentes</strong>, filtrer sur <strong>Échecs</strong>. <button type="button" onClick={() => setExecFilter('failures')} className="text-violet-600 dark:text-violet-400 underline">Filtrer</button></GuideStep>
              <GuideStep n={2}>Distinguer <span className="text-amber-600">terminé avec échecs</span> (assertions KO) de <span className="text-red-600">terminé avec erreurs</span> (incident technique).</GuideStep>
              <GuideStep n={3}>Ouvrir le rapport pour analyser les <strong>cas en échec</strong> et relancer la campagne. <button type="button" onClick={() => setExecFilter('failures')} className="text-violet-600 dark:text-violet-400 underline"><Eye className="inline h-3 w-3" /> Voir</button></GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* (4) Référence : indicateurs */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Info className="h-5 w-5 text-violet-500" />Indicateurs du tableau de bord</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              <tr>{['Indicateur', 'Définition', 'Source'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
              {[
                ['Campagnes totales', 'Nombre total de campagnes (tous statuts) ; sous-total actives / terminées', 'GET /api/automata/campaigns'],
                ['Taux de réussite', 'Tests réussis ÷ total des tests des exécutions en cours (arrondi %)', 'GET /api/automata/executions/{id}/statistics'],
                ['Exécutions actives', 'Exécutions au statut RUNNING', 'GET /api/automata/executions?status=RUNNING'],
                ['Cas de test', 'Somme des cas de test rattachés à toutes les campagnes', 'GET /api/automata/campaigns'],
                ['Résultats de test', 'Cumul réussis / échoués / ignorés (anneau)', 'GET /api/automata/executions/{id}/statistics'],
                ['Répartition par statut', "Nombre de campagnes par statut (barres)", 'GET /api/automata/campaigns'],
              ].map((r, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2 font-medium">{r[0]}</td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{r[1]}</td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">{r[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* (4 bis) Règles de gestion + Endpoints API */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Règles de gestion</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Le tableau de bord se rafraîchit automatiquement toutes les <strong>60 secondes</strong> (bouton Actualiser pour forcer).</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Le <strong>taux de réussite</strong> est calculé sur les exécutions <strong>en cours</strong> ; sans test, il vaut 0 %.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Les campagnes <strong>actives</strong> et les <strong>5 exécutions</strong> les plus récentes sont affichées en priorité.</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" /><strong>Terminé avec échecs</strong> = assertions KO ; <strong>terminé avec erreurs</strong> = incident technique d'exécution.</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Endpoints API</h3>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[
              ['GET', '#10B981', '/api/automata/campaigns'],
              ['GET', '#10B981', '/api/automata/campaigns?activeOnly=true'],
              ['GET', '#10B981', '/api/automata/executions?status=RUNNING'],
              ['GET', '#10B981', '/api/automata/executions/{id}/statistics'],
              ['POST', '#3B82F6', '/api/automata/executions/campaigns/{id}/start'],
              ['POST', '#F59E0B', '/api/automata/executions/{id}/pause'],
              ['POST', '#EF4444', '/api/automata/executions/{id}/stop'],
            ].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : automata_campaign / CampaignController + CampaignExecutionController</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default CampaignSupervisionPOC;
